import { useTranslations } from '@vocab/react';
import type { SaveJobProps } from '../../SaveJob.tsx';
import translations from './.vocab';
import {
  Box,
  Text,
  Button,
  Notice,
  HiddenVisually,
  IconPositive,
  TooltipRenderer,
  ButtonIcon,
  IconBookmark,
} from 'braid-design-system';

const SavedJobForApplied = ({
  jobId,
  isSplitView,
}: {
  jobId: string;
  isSplitView: boolean;
}) => {
  const { t } = useTranslations(translations);
  const title = t('You have applied for this job');

  return isSplitView ? (
    <TooltipRenderer
      id={`serp_applied_${jobId}`}
      tooltip={<Text>{title}</Text>}
    >
      {({ triggerProps }) => (
        <Box {...triggerProps} data={{ automation: 'applied-job' }}>
          <IconPositive tone="positive" />
        </Box>
      )}
    </TooltipRenderer>
  ) : (
    <Box data-automation="applied-job">
      <Notice tone="positive">
        <Text weight="medium">
          <span aria-hidden="true">{t('Applied')}</span>
        </Text>
      </Notice>
      <HiddenVisually>{title}</HiddenVisually>
    </Box>
  );
};

const SaveJobButton = ({
  isSaved,
  isSplitView,
  jobId,
  toggleSavedJob,
}: Omit<SaveJobProps, 'isApplied' | 'isAuthenticated' | 'returnUrl'>) => {
  const { t } = useTranslations(translations);

  const buttonTitle = isSaved
    ? t('Remove from your saved jobs')
    : t('Save job');
  const buttonText = isSaved ? t('Unsave') : t('Save');

  return isSplitView ? (
    <ButtonIcon
      id={`serp-save-job-${jobId}`}
      label={buttonText}
      variant="transparent"
      data={{ automation: isSaved ? 'remove-saved-job' : 'save-job' }}
      icon={
        <IconBookmark
          tone={isSaved ? 'brandAccent' : undefined}
          active={isSaved}
        />
      }
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        toggleSavedJob(e);
      }}
    />
  ) : (
    <Box
      data-automation={isSaved ? 'remove-saved-job' : 'save-job'}
      title={buttonTitle}
    >
      {isSaved && <HiddenVisually>{t('This is a saved job.')}</HiddenVisually>}

      <Button
        tone="neutral"
        variant="transparent"
        bleed
        onClick={toggleSavedJob}
      >
        <Box component="span" paddingRight="xxsmall">
          <IconBookmark
            active={isSaved}
            tone={isSaved ? 'brandAccent' : undefined}
          />
        </Box>
        {buttonText}
      </Button>
    </Box>
  );
};

const SaveJobForSignedIn = ({
  isApplied,
  isSaved,
  isSplitView,
  jobId,
  toggleSavedJob,
}: Omit<SaveJobProps, 'isAuthenticated' | 'returnUrl'>) =>
  isApplied ? (
    <SavedJobForApplied isSplitView={isSplitView} jobId={jobId} />
  ) : (
    <SaveJobButton
      isSaved={isSaved}
      isSplitView={isSplitView}
      jobId={jobId}
      toggleSavedJob={toggleSavedJob}
    />
  );

export default SaveJobForSignedIn;
